import clsx from "clsx";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router";

import { EvalSet } from "@tsmono/inspect-common/types";
import { ErrorPanel, ProgressBar } from "@tsmono/react/components";
import { useProperty } from "@tsmono/react/hooks";
import { directoryRelativeUrl, join } from "@tsmono/util";

import { useLogDir } from "../../app_config";
import {
  useLogsSync,
  type LogListingRow,
  type LogsOverview,
} from "../../log_data";
import { setDocumentTitle } from "../../state/actions";
import { useLogsListing } from "../../state/hooks";
import { useStore } from "../../state/store";
import { useUserSettings } from "../../state/userSettings";
import { FlowButton } from "../flow/FlowButton";
import { useFlowQuery } from "../flow/hooks";
import { ApplicationNavbar } from "../navbar/ApplicationNavbar";
import { NavbarButton } from "../navbar/NavbarButton";
import { ViewSegmentedControl } from "../navbar/ViewSegmentedControl";
import { logsUrl, tasksUrl, useLogRouteParams } from "../routing/url";
import { useEvalSet } from "../server/useEvalSet";
import { ColumnSelectorPopover } from "../shared/ColumnSelectorPopover";

import {
  fileLogIdentity,
  fileLogItem,
  type FileLogItemView,
} from "./fileLogItem";
import { useLogListColumns, type ScoresViewMode } from "./grid/columns/hooks";
import type { LogListRow } from "./grid/columns/types";
import { LogListGrid } from "./grid/LogListGrid";
import { buildLogListRow } from "./grid/logListRow";
import { useLogListData } from "./grid/useLogListData";
import type { LogsListingDescriptor } from "./listing/useLogsListingQuery";
import { FolderLogItem, PendingTaskItem } from "./LogItem";
import { LogListFooter } from "./LogListFooter";
import styles from "./LogsPanel.module.css";
import { useLogsOverview } from "./useLogsOverview";

export type LogsPanelMode = "logs" | "tasks";

interface LogsPanelProps {
  maybeShowSingleLog?: boolean;
  mode?: LogsPanelMode;
}

export const LogsPanel: FC<LogsPanelProps> = ({
  maybeShowSingleLog,
  mode = "logs",
}) => {
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [columnButtonEl, setColumnButtonEl] =
    useState<HTMLButtonElement | null>(null);

  const showRetriedLogs = useUserSettings((state) => state.showRetriedLogs);
  const setShowRetriedLogs = useUserSettings(
    (state) => state.setShowRetriedLogs
  );
  const logDir = useLogDir();
  const { gridStateByScope, patchGridState } = useLogsListing();

  const { logPath } = useLogRouteParams();
  const evalSet = useEvalSet(logPath || "").data;
  // Sync the listing for this panel's scope; the error panel and busy
  // indications derive from its status folded with the overview read's own
  // loading/error.
  const sync = useLogsSync(logDir, logPath ?? "");

  const currentDir = join(logPath || "", logDir);

  // Identifies the current data scope for the log list. Each scope keeps
  // its own filter/sort independently in the store: Tasks at the root and
  // Folders at the root are distinct scopes, so toggling between them
  // restores each side's state instead of clearing. Folder drill-down is
  // a different scope (different dir), so each folder also remembers its
  // own state. `undefined` until logDir hydrates so we never write under
  // a half-initialized scope.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const scopeKey = logDir === undefined ? undefined : `${mode}::${currentDir}`;

  // Cache identity of the row universe: the listing/overview queries depend
  // on everything the view mapping reads, so it carries the display toggle
  // along with the view scope.
  const universe =
    scopeKey === undefined
      ? undefined
      : `${scopeKey}::retried=${showRetriedLogs}`;

  const flowData = useFlowQuery(logPath || "").data;

  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    setDocumentTitle({
      logDir: logDir,
    });
  }, [logDir]);

  const itemView: FileLogItemView = useMemo(
    () => ({ mode, logDir, currentDir, showRetriedLogs }),
    [mode, logDir, currentDir, showRetriedLogs]
  );

  const isCandidate = useCallback(
    (log: LogListingRow) => fileLogIdentity(log.name, itemView) !== undefined,
    [itemView]
  );
  const overviewQuery = useLogsOverview({
    logDir,
    universe,
    view: {
      folderDir: mode === "logs" ? currentDir : undefined,
      showRetriedLogs,
      isCandidate,
    },
  });
  const overview = overviewQuery.data;

  const busy = sync.busy;
  // The navbar bar tracks the sync round-trip only — engine background
  // fetching (`busy`) stays in the footer/overlay indications.
  const navbarLoading = sync.loading || overviewQuery.loading;

  // Presentation items with no database record: folders (pinned) and the
  // eval set's not-yet-run tasks. File rows come from the listing query.
  const logItems: Array<FolderLogItem | PendingTaskItem> = useMemo(() => {
    const currentDirRelative = directoryRelativeUrl(currentDir, logDir);
    const folderItems: Array<FolderLogItem | PendingTaskItem> = (
      overview?.folders ?? []
    ).map((folder) => ({
      id: folder.name,
      name: folder.name,
      type: "folder",
      url: logsUrl(
        join(folder.name, decodeURIComponent(currentDirRelative)),
        logDir
      ),
      itemCount: folder.itemCount,
    }));
    return appendPendingItems(evalSet, new Set(overview?.taskIds), folderItems);
  }, [overview, evalSet, currentDir, logDir]);

  const hasRetriedLogs = (overview?.retriedCount ?? 0) > 0;

  // In the folder view, scope the Metrics list to logs under the current
  // directory so descending into a subfolder shows only that folder's metrics.
  // The flat tasks view shows columns across the whole set.
  const scopeDir = mode === "logs" ? currentDir : undefined;

  // Shared view-mode state for the scorer columns. The same useProperty
  // scope/key is read by LogListGrid so the grid and popover stay in sync,
  // and by persisting via useProperty the choice survives reloads.
  const [scoresViewMode, setScoresViewMode] = useProperty<ScoresViewMode>(
    "log-list-scores-view",
    "mode",
    { defaultValue: "by-metric" }
  );

  // LogsPanel uses `pickerColumns` for the popover so it only shows the
  // active view mode's checkboxes; the grid (LogListGrid) reads `columns`
  // from its own `useLogListColumns` call and gets both sets for stability.
  const {
    pickerColumns,
    visibility,
    setColumnVisibility,
    getValue,
    getComparator,
    getFilterType,
    accessorsKey,
  } = useLogListColumns(mode, scopeDir, scoresViewMode);

  const toRow = useCallback(
    (log: LogListingRow) => {
      const item = fileLogItem(log, itemView);
      return item === undefined ? undefined : buildLogListRow(item);
    },
    [itemView]
  );

  // One descriptor shared by the row query (useLogListData) and the grid's
  // find-band match query, so they can never disagree about the universe.
  const listing = useMemo<LogsListingDescriptor<LogListRow>>(
    () => ({
      logDir,
      // Match the row universe: folder mode lists the current directory, the
      // flat tasks view lists the whole log dir (like `scopeDir` above) — a
      // narrower scan prefix would silently drop matching rows outside it.
      prefix: mode === "logs" ? currentDir : logDir,
      universe,
      toRow,
    }),
    [logDir, mode, currentDir, universe, toRow]
  );

  const listData = useLogListData({
    overlayItems: logItems,
    scopeKey,
    getValue,
    getComparator,
    getFilterType,
    accessorsKey,
    listing,
  });

  // Pre-filter row count — distinguishes "no items yet" (loading
  // empty-state) from "filters matched nothing".
  const totalRowCount = (overview?.fileCount ?? 0) + logItems.length;

  // The listing/overview queries are asynchronous by design: fold their
  // first-read window into the busy indication so the grid shows "syncing"
  // rather than a silently empty list.
  const listBusy = busy || listData.pending || overviewQuery.loading;
  const error = sync.error ?? overviewQuery.error ?? listData.error;

  const currentColumnVisibility = useStore(
    (state) => state.logs.listing.columnVisibility
  );

  // Active per-column filters for this scope (drives the Reset button + the
  // Columns popover's filter markers).
  const filteredFields = useMemo(
    () => Object.keys(listData.columnFilters ?? {}),
    [listData.columnFilters]
  );
  const hasFilter = filteredFields.length > 0;

  const handleResetFilters = useCallback(() => {
    if (scopeKey) patchGridState(scopeKey, { columnFilters: {} });
  }, [scopeKey, patchGridState]);

  // The popover only sees `pickerColumns` (the active view mode) and emits a
  // full map for them, so persist only the entries that differ from the
  // effective visibility — persisting the whole map would freeze untouched
  // columns' mode-dependent defaults (one stored map spans tasks/logs modes).
  // Hiding a column also clears any active filter on it (matches the prior
  // grid — a hidden column shouldn't keep filtering invisibly).
  const handleColumnVisibilityChange = useCallback(
    (newVisibility: Record<string, boolean>) => {
      const changed = Object.fromEntries(
        Object.entries(newVisibility).filter(
          ([field, visible]) => visibility[field] !== visible
        )
      );
      if (Object.keys(changed).length === 0) return;
      if (scopeKey) {
        const cf = gridStateByScope[scopeKey]?.columnFilters;
        if (cf) {
          const next = Object.fromEntries(
            Object.entries(cf).filter(([id]) => changed[id] !== false)
          );
          if (Object.keys(next).length !== Object.keys(cf).length) {
            patchGridState(scopeKey, { columnFilters: next });
          }
        }
      }
      setColumnVisibility({ ...currentColumnVisibility, ...changed });
    },
    [
      visibility,
      currentColumnVisibility,
      setColumnVisibility,
      scopeKey,
      gridStateByScope,
      patchGridState,
    ]
  );

  const progress = useMemo(() => {
    const pendingTasks = logItems.filter(
      (item) => item.type === "pending-task"
    ).length;
    const total = (overview?.fileCount ?? 0) + pendingTasks;
    const running = (overview?.startedCount ?? 0) + pendingTasks;
    return {
      complete: total - running,
      total,
    };
  }, [logItems, overview]);

  // Single-log workspaces skip the pointless one-row list. `replace` keeps
  // this page out of history so back from the log doesn't bounce forward
  // again. Deliberately not gated on sync settling — see the audit doc.
  const soleItemUrl = soleItemRedirectUrl(overview, logItems, itemView);
  if (maybeShowSingleLog && soleItemUrl) {
    return <Navigate to={soleItemUrl} replace />;
  }

  return (
    <div className={clsx(styles.panel)}>
      <ApplicationNavbar
        fnNavigationUrl={mode === "tasks" ? tasksUrl : logsUrl}
        currentPath={mode === "tasks" ? undefined : logPath}
        loading={navbarLoading}
        viewerOptionsError={sync.refreshError}
      >
        {hasFilter && (
          <NavbarButton
            key="reset-filters"
            label="Reset Filters"
            icon="filter"
            onClick={handleResetFilters}
          />
        )}

        {hasRetriedLogs && (
          <NavbarButton
            key="show-retried"
            label="Show Retried Logs"
            icon={showRetriedLogs ? "toggleOn" : "toggleOff"}
            latched={showRetriedLogs}
            subtle
            onClick={() => setShowRetriedLogs(!showRetriedLogs)}
          />
        )}

        <NavbarButton
          key="choose-columns"
          ref={setColumnButtonEl}
          label="Columns"
          icon="columns"
          dropdown
          subtle
          onClick={(e) => {
            e.stopPropagation();
            setShowColumnSelector((prev) => !prev);
          }}
        />

        <ViewSegmentedControl
          selectedSegment={mode === "tasks" ? "tasks" : "logs"}
        />
        {flowData && <FlowButton />}
      </ApplicationNavbar>

      <ColumnSelectorPopover
        showing={showColumnSelector}
        setShowing={setShowColumnSelector}
        columns={pickerColumns}
        visibility={visibility}
        onVisibilityChange={handleColumnVisibilityChange}
        positionEl={columnButtonEl}
        filteredFields={filteredFields}
        scoresHeading="Metrics"
        groupableScores
        scoresViewMode={scoresViewMode}
        onScoresViewModeChange={setScoresViewMode}
      />

      {error ? (
        <ErrorPanel
          title="Error"
          error={{ message: error.message, stack: error.stack }}
        />
      ) : (
        <>
          <div className={clsx(styles.list, "text-size-smaller")}>
            {/* Keyed on the scope so switching folder/mode resets the grid
                wholesale — scroll, selection, and the find band's state
                (term + matches) belong to one scope and reset together. */}
            <LogListGrid
              key={scopeKey ?? "pending"}
              rows={listData.rows}
              totalRowCount={totalRowCount}
              sorting={listData.sorting}
              columnFilters={listData.columnFilters}
              filter={listData.filter}
              orderBy={listData.orderBy}
              currentPath={currentDir}
              scopeKey={scopeKey}
              mode={mode}
              busy={listBusy}
              listing={listing}
            />
          </div>
          <LogListFooter
            itemCount={totalRowCount}
            filteredCount={listData.filteredCount}
            progressText={listBusy ? "Syncing data" : undefined}
            progressBar={
              progress.total !== progress.complete ? (
                <ProgressBar
                  min={0}
                  max={progress.total}
                  value={progress.complete}
                  width="100px"
                />
              ) : undefined
            }
          />
        </>
      )}
    </div>
  );
};

/** The redirect target when the view holds exactly one item (folder, file,
 *  or pending task — the latter has no url, so no redirect). */
const soleItemRedirectUrl = (
  overview: LogsOverview | undefined,
  logItems: Array<FolderLogItem | PendingTaskItem>,
  itemView: FileLogItemView
): string | undefined => {
  if ((overview?.fileCount ?? 0) + logItems.length !== 1) return undefined;
  if (logItems.length === 1) return logItems[0]?.url;
  return overview?.soleFileName !== undefined
    ? fileLogIdentity(overview.soleFileName, itemView)?.url
    : undefined;
};

const appendPendingItems = (
  evalSet: EvalSet | undefined,
  tasksWithLogFiles: Set<string>,
  items: Array<FolderLogItem | PendingTaskItem>
): Array<FolderLogItem | PendingTaskItem> => {
  const pendingTasks = new Array<PendingTaskItem>();
  for (const task of evalSet?.tasks || []) {
    if (!tasksWithLogFiles.has(task.task_id)) {
      pendingTasks.push({
        id: task.task_id,
        name: task.name || "<unknown>",
        model: task.model,
        type: "pending-task",
      });
    }
  }

  // Sort pending tasks by name
  pendingTasks.sort((a, b) => a.name.localeCompare(b.name));

  items.push(...pendingTasks);

  return items;
};
